// The source viewer's three tabs (#89): where the source is used (E-05),
// its facts in the assistant's own words (E-15), and what is known about it
// (E-16). Each card carries the number its marker carries in the centre;
// selecting a card selects the pair. Pure projection + intents; the facts
// filter is view-ephemeral.

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { LoadedSource } from "../../app/ports/resource.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import type { MaterialRef } from "../Navigation/types.js";
import { SourceSummary } from "../SourceSummary/SourceSummary.js";
import type { SourceUse } from "../SourceView/types.js";
import { Text } from "../Text/Text.js";
import {
    type FactFilter,
    filterFacts,
    type NumberedFact,
    reviewCount,
    type UseGroup,
} from "./sourceViewerModel.js";

const Number_ = ({
    n,
    selected,
}: {
    readonly n: number;
    readonly selected: boolean;
}): ReactNode => (
    <span className="sv-num" data-selected={selected ? "true" : undefined}>
        {n}
    </span>
);

// === Used in (E-05) ==========================================================

export const UsedInTab = ({
    groups,
    numbered,
    selected,
    onSelect,
    onOpenUse,
    onOpenMaterial,
}: {
    readonly groups: readonly UseGroup[];
    // Whether the centre numbers its marks (PDF pages, read text); elsewhere the
    // uses are not numbered.
    readonly numbered: boolean;
    readonly selected: number | undefined;
    readonly onSelect: (n: number) => void;
    readonly onOpenUse: (use: SourceUse) => void;
    readonly onOpenMaterial: (material: MaterialRef) => void;
}): ReactNode => {
    const { t } = useLingui();
    if (groups.length === 0)
        return (
            <Text as="p" size="sm" tone="dim" className="sv-empty">
                {t`Not used in any material yet.`}
            </Text>
        );
    // One card per use, as pen draws them (E-06): its number, where it is
    // (the material, and the page when known) and the statement that relies
    // on the source. A material made from the source with no statement of
    // its own still shows, as one plain card.
    return (
        <div className="sv-list">
            {groups.flatMap((g) => [
                ...g.uses.map((u) => (
                    <div
                        key={u.n}
                        className="sv-card"
                        data-selected={u.n === selected ? "true" : undefined}
                    >
                        <button
                            type="button"
                            className="sv-card__select"
                            aria-pressed={u.n === selected}
                            onClick={() => onSelect(u.n)}
                        >
                            {numbered ? (
                                <Number_ n={u.n} selected={u.n === selected} />
                            ) : null}
                            <span className="sv-card__text">
                                <span className="sv-card__where">
                                    {u.page === undefined
                                        ? g.material.title
                                        : t`${g.material.title} · p. ${u.page}`}
                                </span>
                                <span className="sv-card__quote">
                                    {u.statement}
                                </span>
                            </span>
                        </button>
                        <IconButton
                            label={t`Open in ${g.material.title}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => onOpenUse(u)}
                        >
                            <Icon name="arrow-up-right" size="sm" />
                        </IconButton>
                    </div>
                )),
                ...(g.madeFrom && g.uses.length === 0
                    ? [
                          <div
                              key={`made-${g.material.id}`}
                              className="sv-card sv-card--plain"
                          >
                              <span className="sv-card__text">
                                  <span className="sv-card__where">
                                      {g.material.title}
                                  </span>
                                  <Text as="span" size="sm" tone="dim">
                                      {t`Made from this source`}
                                  </Text>
                              </span>
                              <IconButton
                                  label={t`Open ${g.material.title}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onOpenMaterial(g.material)}
                              >
                                  <Icon name="arrow-up-right" size="sm" />
                              </IconButton>
                          </div>,
                      ]
                    : []),
            ])}
        </div>
    );
};

// === Facts (E-15) ============================================================

export const FactsTab = ({
    facts,
    selected,
    onSelect,
}: {
    readonly facts: readonly NumberedFact[];
    readonly selected: number | undefined;
    readonly onSelect: (n: number) => void;
}): ReactNode => {
    const { t } = useLingui();
    // View-ephemeral: which facts are shown.
    const [filter, setFilter] = useState<FactFilter>("all");
    if (facts.length === 0)
        return (
            <Text as="p" size="sm" tone="dim" className="sv-empty">
                {t`No facts from this source yet. The assistant writes them down as it reads the source for a material.`}
            </Text>
        );
    const review = reviewCount(facts);
    type Chip = { readonly value: FactFilter; readonly label: string };
    // "Review" only while something needs it.
    const reviewChip: readonly Chip[] =
        review > 0 ? [{ value: "review", label: t`Review  ${review}` }] : [];
    const filters: readonly Chip[] = [
        { value: "all", label: t`All` },
        { value: "used", label: t`Used` },
        { value: "unused", label: t`Unused` },
        ...reviewChip,
    ];
    const active = filters.some((f) => f.value === filter) ? filter : "all";
    const shown = filterFacts(facts, active);
    return (
        <div className="sv-list">
            <div className="sv-facts-head">
                <AiMarker variant="tag" label={t`Extracted`} />
                <Text as="span" size="sm" tone="dim">
                    {t`${plural(facts.length, {
                        one: "# fact in the assistant's own words",
                        other: "# facts in the assistant's own words",
                    })}`}
                </Text>
            </div>
            <fieldset className="sv-filters" aria-label={t`Show`}>
                {filters.map((f) => (
                    <button
                        key={f.value}
                        type="button"
                        className="sv-filter"
                        aria-pressed={f.value === active}
                        onClick={() => setFilter(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </fieldset>
            {shown.length === 0 ? (
                <Text as="p" size="sm" tone="dim" className="sv-empty">
                    {t`No facts here.`}
                </Text>
            ) : (
                shown.map((f) => {
                    const page = f.passage?.page;
                    const located = f.passage?.located === true;
                    return (
                        <button
                            key={f.claim.claimId}
                            type="button"
                            className="sv-card sv-card__select sv-fact"
                            data-selected={
                                f.n === selected ? "true" : undefined
                            }
                            aria-pressed={f.n === selected}
                            onClick={() => onSelect(f.n)}
                        >
                            <Number_ n={f.n} selected={f.n === selected} />
                            <span className="sv-card__text">
                                <span className="sv-card__quote">
                                    {f.claim.wording}
                                </span>
                                {located ? (
                                    <span className="sv-fact__ok">
                                        <Icon name="check" size="xs" />
                                        {t`Checked against the source`}
                                    </span>
                                ) : (
                                    <span className="sv-fact__review">
                                        <Icon name="warning" size="xs" />
                                        {t`The passage is no longer in the source`}
                                    </span>
                                )}
                                <span className="sv-fact__line">
                                    <span className="sv-fact__where">
                                        {page === undefined
                                            ? ""
                                            : t`p. ${page}`}
                                    </span>
                                    <span>
                                        {f.usedIn === 0
                                            ? t`Not used yet`
                                            : t`Used in ${f.usedIn}`}
                                    </span>
                                </span>
                            </span>
                        </button>
                    );
                })
            )}
        </div>
    );
};

// === Details (E-16) ==========================================================

export const DetailsTab = ({
    source,
    kind,
    size,
    readLine,
    onRemove,
}: {
    readonly source: LoadedSource;
    readonly kind: string;
    readonly size: string | undefined;
    readonly readLine: string;
    readonly onRemove: () => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const added = (() => {
        const iso = source.meta.addedOnIso;
        if (iso === undefined) return undefined;
        const d = new Date(iso);
        return Number.isNaN(d.getTime())
            ? undefined
            : d.toLocaleDateString(i18n.locale, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
              });
    })();
    const row = (label: string, value: ReactNode) => (
        <div className="sv-dl__row">
            <dt>{label}</dt>
            <dd>{value}</dd>
        </div>
    );
    const notes = source.annotations.filter((a) => a.text.trim() !== "");
    return (
        <div className="sv-list sv-list--details">
            <section className="sv-details">
                <h2 className="sv-details__label">{t`About the source`}</h2>
                <dl className="sv-dl">
                    {row(t`File`, source.meta.filename)}
                    {row(
                        t`Type`,
                        size === undefined ? kind : `${kind} · ${size}`,
                    )}
                    {added === undefined ? null : row(t`Added`, added)}
                    {row(
                        t`Origin`,
                        source.original === undefined
                            ? t`Added as text`
                            : t`Uploaded file`,
                    )}
                </dl>
            </section>
            <section className="sv-details">
                <h2 className="sv-details__label">{t`What was read`}</h2>
                <dl className="sv-dl">{row(t`Text`, readLine)}</dl>
                {source.summary === undefined ? null : (
                    <div className="sv-details__summary">
                        <SourceSummary summary={source.summary} />
                    </div>
                )}
            </section>
            {notes.length === 0 ? null : (
                <section className="sv-details">
                    <h2 className="sv-details__label">{t`Your notes`}</h2>
                    <ul className="sv-notes">
                        {notes.map((n) => (
                            <li key={n.id}>{n.text}</li>
                        ))}
                    </ul>
                </section>
            )}
            <div className="sv-details__actions">
                <Button variant="danger" size="sm" onClick={onRemove}>
                    {t`Remove from Uploaded sources…`}
                </Button>
            </div>
        </div>
    );
};
